import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useEmblaCarousel from "embla-carousel-react";

/**
 * SimpleCarousel — a minimal drop-in replacement for AliceCarousel (subset)
 * Supported props (subset used in this app):
 * - className: string
 * - items: ReactNode[]
 * - responsive: { [minWidth: number]: { items: number } }
 * - autoPlay: boolean
 * - autoPlayInterval: number (ms)
 * - infinite: boolean (loop)
 * - mouseTracking: boolean (dragging enabled by default)
 * - renderDotsItem?: (e: { isActive: boolean }) => React.ReactNode
 * - renderPrevButton?: (e: { isDisabled: boolean }) => React.ReactNode
 * - renderNextButton?: (e: { isDisabled: boolean }) => React.ReactNode
 * - disableDotsControls?: boolean
 * - disableButtonsControls?: boolean
 */
export default function SimpleCarousel(props) {
  const {
    className,
    items = [],
    responsive = { 0: { items: 1 } },
    autoPlay = false,
    autoPlayInterval = 3000,
    infinite = false,
    // mouseTracking not required, Embla supports dragging by default
    renderDotsItem,
    renderPrevButton,
    renderNextButton,
    disableDotsControls = false,
    disableButtonsControls = false,
  } = props;

  // Determine items per view from responsive breakpoints
  const getItemsPerView = useCallback(() => {
    if (!responsive || typeof window === "undefined") return 1;
    const breakpoints = Object.keys(responsive)
      .map((n) => Number(n))
      .sort((a, b) => a - b);
    const width = window.innerWidth || 0;
    let selected = responsive[breakpoints[0]]?.items || 1;
    for (const bp of breakpoints) {
      if (width >= bp) selected = responsive[bp]?.items || selected;
    }
    return Math.max(1, Number(selected) || 1);
  }, [responsive]);

  const [slidesToShow, setSlidesToShow] = useState(getItemsPerView());

  useEffect(() => {
    const onResize = () => setSlidesToShow(getItemsPerView());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [getItemsPerView]);

  const emblaOptions = useMemo(
    () => ({
      loop: !!infinite,
      // Align to start so multiple slides show correctly
      align: "start",
      // Speed can be tuned; default is fine
    }),
    [infinite]
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    onSelect();
    emblaApi.on("select", onSelect);
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi, onSelect]);

  // Autoplay
  const autoplayTimer = useRef(null);
  useEffect(() => {
    if (!autoPlay || !emblaApi) return;
    const play = () => {
      if (!emblaApi) return;
      if (infinite) emblaApi.scrollNext();
      else {
        if (emblaApi.canScrollNext()) emblaApi.scrollNext();
        else emblaApi.scrollTo(0);
      }
    };
    autoplayTimer.current = setInterval(play, autoPlayInterval);
    return () => {
      if (autoplayTimer.current) clearInterval(autoplayTimer.current);
    };
  }, [autoPlay, autoPlayInterval, emblaApi, infinite]);

  const canScrollPrev = emblaApi ? emblaApi.canScrollPrev() : false;
  const canScrollNext = emblaApi ? emblaApi.canScrollNext() : false;

  const slideStyle = useMemo(
    () => ({
      flex: `0 0 ${100 / (slidesToShow || 1)}%`,
      minWidth: `${100 / (slidesToShow || 1)}%`,
      padding: "1rem",
    }),
    [slidesToShow]
  );

  return (
    <div className={className} style={{ overflow: "hidden" }}>
      {/* Viewport */}
      <div ref={emblaRef} style={{ overflow: "hidden" }}>
        <div style={{ display: "flex", gap: "1rem" }}>
          {items.map((child, index) => (
            <div key={index} style={slideStyle}>
              {child}
            </div>
          ))}
        </div>
      </div>

      {/* Arrows */}
      {!disableButtonsControls && (renderPrevButton || renderNextButton) && (
        <>
          {renderPrevButton && (
            <div
              onClick={() => emblaApi && emblaApi.scrollPrev()}
              style={{ display: "inline-block" }}
            >
              {renderPrevButton({ isDisabled: !infinite && !canScrollPrev })}
            </div>
          )}
          {renderNextButton && (
            <div
              onClick={() => emblaApi && emblaApi.scrollNext()}
              style={{ display: "inline-block" }}
            >
              {renderNextButton({ isDisabled: !infinite && !canScrollNext })}
            </div>
          )}
        </>
      )}

      {/* Dots */}
      {!disableDotsControls && renderDotsItem && scrollSnaps.length > 1 && (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {scrollSnaps.map((_, idx) => (
            <li
              key={idx}
              onClick={() => emblaApi && emblaApi.scrollTo(idx)}
              style={{ display: "inline-block" }}
            >
              {renderDotsItem({ isActive: idx === selectedIndex })}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
